import { observer } from 'mobx-react-lite'
import Styles from '../Panels.module.scss'
import PlagiarismDetectionStyles from './PlagiarismDetection.module.scss'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import {
  PlagiarismDetectionResponse,
  PlagiarismDetectionResult,
  createPlagiarismDetectionRequest,
} from 'components/DocRightPanelWrapper/createPlagiarismDetectionRequest'
import { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { ReactComponent as Close } from 'assets/icons/close16.svg'
import cx from 'classnames'
import { findNodeByType } from 'common/Prose/utils/findNodeByType'
import { Node } from 'prosemirror-model'
import { schema } from 'prose-core'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button/Button'
import { CheckBroken, Scan } from 'blaze-ui/icons/20'
import { Heading } from 'blaze-ui/Heading/Heading'
import { PlagiarismCheck } from 'blaze-ui/icons/36'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { useRootStore } from 'stores/RootStore'
import { useShowCreditsPaywall } from 'hooks/useShowCreditsPaywall'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { usePurchaseContentCreditsModal } from 'hooks/usePurchaseContentCreditsModal'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'

const PlagiarismDetectionItem = ({ result }: { result: PlagiarismDetectionResult }) => {
  return (
    <a
      href={result.url}
      target="_blank"
      rel="noopener noreferrer"
      className={PlagiarismDetectionStyles.plagiarismResult}
    >
      <Text lineClamp={1} variant="smallList">
        <div className={PlagiarismDetectionStyles.title}>{result.title}</div>
      </Text>
      <Text lineClamp={1} variant="label">
        <div className={PlagiarismDetectionStyles.url}>{result.url}</div>
      </Text>
      <Text lineClamp={3} variant="secondary">
        <div className={PlagiarismDetectionStyles.content}>{result.textsnippet}</div>
      </Text>
    </a>
  )
}

export const usePlagiarsimDetection = () => {
  const [loading, setLoading] = useState(false)
  const [plagiarismResult, setPlagiarismResult] = useState<PlagiarismDetectionResponse>()
  const [hasRun, setHasRun] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const { usageCosts, creditsRemaining } = useCreditUsage()
  const { price, isTrackingCredits } = useShowCreditsPaywall()
  const { tierUsageStore } = useRootStore()
  const subscription = tierUsageStore.currentSubscription
  const isTrackingCreditsOrPioneer = isTrackingCredits || subscription?.plan === PaymentPlanKey.VirtualMarketer
  const generationCost = usageCosts?.plagiarism_credits_cost ?? 0
  const isPaid = tierUsageStore.currentSubscription?.status === 'active'
  const hasPriceOrUpgradePath = (isPaid && !!price) || !isPaid
  const hasInsufficientCredits =
    isTrackingCreditsOrPioneer && creditsRemaining < generationCost && hasPriceOrUpgradePath
  const canShowPaywall = isTrackingCreditsOrPioneer && creditsRemaining < generationCost

  const run = async (text: string) => {
    setError(undefined)
    setLoading(true)
    try {
      const res = await createPlagiarismDetectionRequest(text)
      if (res.data.error || res.type === 'error') {
        // these are errors from the copyscape API
        setError(res.data.error || 'Something went wrong')
      } else {
        setPlagiarismResult(res.data as PlagiarismDetectionResponse)
      }
    } catch {
      setPlagiarismResult(undefined)
      setError('Something went wrong')
    }
    setLoading(false)
    setHasRun(true)
  }

  return {
    canShowPaywall,
    loading,
    plagiarismResult,
    hasRun,
    run,
    error,
    setError,
    generationCost,
    hasInsufficientCredits,
    hasPriceOrUpgradePath,
  }
}

export const PlagiarismDetection = observer(({ children }: PropsWithChildren) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const workspace = useCurrentWorkspace()
  const { tierUsageStore } = useRootStore()
  const { currentSubscription } = tierUsageStore
  const { price } = useShowCreditsPaywall()
  const { usageCosts } = useCreditUsage()
  const openPurchaseContentCreditsModal = usePurchaseContentCreditsModal()
  const {
    canShowPaywall,
    loading,
    plagiarismResult,
    hasRun,
    run,
    error,
    setError,
    generationCost,
    hasInsufficientCredits,
    hasPriceOrUpgradePath,
  } = usePlagiarsimDetection()
  const view = useEditorView()
  const isPlagiarismCreditsEnabled = useFlipperFlag('plagiarism_credits')

  const showInsufficientCreditsModal = useCallback(
    (onSuccess?: () => void) => {
      const isPaid = currentSubscription?.status === 'active'
      const hasPriceForAddCredits = !!price
      if (isPaid && !hasPriceForAddCredits) return
      if (!price) return

      const cost = usageCosts?.plagiarism_credits_cost ?? 0
      openPurchaseContentCreditsModal(
        {
          price,
          onSuccess,
          defaultBatchCount: onSuccess ? Math.ceil(cost / 100) || 1 : 1,
        },
        { newStack: true },
      )
    },
    [currentSubscription?.status, price, usageCosts?.plagiarism_credits_cost, openPurchaseContentCreditsModal],
  )

  const handleScan = useCallback(
    async (aiNode: Node | null, text: string) => {
      if (aiNode) {
        const node = Node.fromJSON(schema, { type: 'doc', content: JSON.parse(aiNode.attrs.contentData) })
        run(node.textContent)
      } else if (text.length > 0) {
        run(text)
      }
    },
    [run],
  )

  const handlePress = useCallback(async () => {
    const aiNode = findNodeByType(view.state.doc, schema.nodes.ai_suggestion)
    const text = view.state.doc.textBetween(0, view.state.doc.nodeSize - 2)
    if (!aiNode && text.length === 0) {
      setError('Document is empty')
      return
    }

    if (!workspace?.is_virtual_marketer || !isPlagiarismCreditsEnabled) {
      return handleScan(aiNode, text)
    }

    if (canShowPaywall && hasPriceOrUpgradePath) return showInsufficientCreditsModal(() => handleScan(aiNode, text))

    return handleScan(aiNode, text)
  }, [
    setError,
    workspace?.is_virtual_marketer,
    isPlagiarismCreditsEnabled,
    canShowPaywall,
    hasPriceOrUpgradePath,
    showInsufficientCreditsModal,
    handleScan,
    view.state.doc,
  ])

  let content

  if (error || (hasRun && !plagiarismResult?.result)) {
    content = (
      <div className={cx(PlagiarismDetectionStyles.placeholder, PlagiarismDetectionStyles.success)}>
        <div className={cx(PlagiarismDetectionStyles.svgWrapper, PlagiarismDetectionStyles.failure)}>
          <Close />
        </div>
        <Heading level={5}>
          <span className={PlagiarismDetectionStyles.loadingText}>{error}</span>
        </Heading>
        <Text variant="secondary">
          <span className={PlagiarismDetectionStyles.wordCount}>Please try again</span>
        </Text>
      </div>
    )
  } else if (loading) {
    content = (
      <div className={PlagiarismDetectionStyles.placeholder}>
        <div className={PlagiarismDetectionStyles.spinner} />
        <Text variant="secondary">
          <span className={PlagiarismDetectionStyles.loadingText}>Scanning for plagiarism...</span>
        </Text>
      </div>
    )
  } else if (!hasRun) {
    content = (
      <div className={PlagiarismDetectionStyles.placeholder}>
        {!isSystemRedesignEnabled && <PlagiarismCheck size={45} />}
        <Text variant="placeholder">Any articles or websites with content matching yours will appear here.</Text>
      </div>
    )
  } else if (hasRun && plagiarismResult?.result?.length) {
    content = (
      <div className={PlagiarismDetectionStyles.results}>
        <div className={PlagiarismDetectionStyles.resultsHeader}>
          <Text variant={isSystemRedesignEnabled ? 'primary' : 'largeList'}>
            Found {plagiarismResult.result.length} results
          </Text>
        </div>
        <div className={PlagiarismDetectionStyles.resultsList}>
          {plagiarismResult.result.map((result) => (
            <PlagiarismDetectionItem result={result} key={result.index} />
          ))}
        </div>
      </div>
    )
  } else if (hasRun && !plagiarismResult?.result?.length) {
    content = (
      <div className={cx(PlagiarismDetectionStyles.placeholder, PlagiarismDetectionStyles.success)}>
        <div className={PlagiarismDetectionStyles.svgWrapper}>
          <CheckBroken size={24} />
        </div>
        <Heading level={4}>
          <span className={PlagiarismDetectionStyles.successTitle}>No plagiarism detected!</span>
        </Heading>
        <Text variant="secondary">
          <span className={PlagiarismDetectionStyles.wordCount}>{plagiarismResult?.querywords} words scanned.</span>
        </Text>
      </div>
    )
  }

  const scanButton = useMemo(() => {
    if (!isPlagiarismCreditsEnabled || !workspace?.is_virtual_marketer) {
      return (
        <Button
          size={isSystemRedesignEnabled ? 'md' : 'lg'}
          variant={isSystemRedesignEnabled ? 'secondary' : 'primary'}
          onPress={handlePress}
          frontIcon={Scan}
          isDisabled={loading}
        >
          Scan Document
        </Button>
      )
    }
    if (hasInsufficientCredits) {
      return (
        <Button
          size={isSystemRedesignEnabled ? 'md' : 'lg'}
          variant="primary"
          onPress={() => showInsufficientCreditsModal()}
        >
          <span className={PlagiarismDetectionStyles.plagiarismDetectionButton}>
            Add Credits <Credits size={20} /> {generationCost}
          </span>
        </Button>
      )
    }
    return (
      <Button
        size={isSystemRedesignEnabled ? 'md' : 'lg'}
        variant="primary"
        onPress={handlePress}
        frontIcon={Scan}
        isDisabled={loading}
      >
        <span className={PlagiarismDetectionStyles.plagiarismDetectionButton}>
          Scan Document <Credits size={20} /> {generationCost}
        </span>
      </Button>
    )
  }, [
    isSystemRedesignEnabled,
    handlePress,
    loading,
    hasInsufficientCredits,
    generationCost,
    showInsufficientCreditsModal,
    isPlagiarismCreditsEnabled,
    workspace?.is_virtual_marketer,
  ])

  return (
    <div className={Styles.container}>
      <Text variant={isSystemRedesignEnabled ? 'secondary' : 'primary'}>
        Scan the contents of this document for originality, compared against online sources.
      </Text>
      {scanButton}
      {content}
      {children}
    </div>
  )
})
